import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Divider, Chip, Button, 
  FormControl, Select, MenuItem, CircularProgress, TextField, Stack,
  Tooltip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import LanguageIcon from '@mui/icons-material/Language';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { booksAPI } from '../../services/api';
import CategoryManager from '../categories/CategoryManager';
import BookCover from './BookCover';

const BookHeaderCard = ({ book, categories, onStatusChange, setSnackbar }) => {
  // Status updating state
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  
  // Title and author editing state
  const [titleEditing, setTitleEditing] = useState(false);
  const [authorEditing, setAuthorEditing] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newAuthor, setNewAuthor] = useState('');
  const [metadataUpdateLoading, setMetadataUpdateLoading] = useState(false);

  // Cover update state
  const [coverUpdateLoading, setCoverUpdateLoading] = useState(false);

  // Description and key ideas editing state
  const [descriptionEditing, setDescriptionEditing] = useState(false);
  const [newDescription, setNewDescription] = useState('');

  // Add new state variables for additional fields
  const [subtitleEditing, setSubtitleEditing] = useState(false);
  const [publisherEditing, setPublisherEditing] = useState(false);
  const [publishedYearEditing, setPublishedYearEditing] = useState(false);
  const [pageCountEditing, setPageCountEditing] = useState(false);
  const [isbnEditing, setIsbnEditing] = useState(false);
  const [ratingEditing, setRatingEditing] = useState(false);
  const [languageEditing, setLanguageEditing] = useState(false); // Add language editing state
  
  const [newSubtitle, setNewSubtitle] = useState('');
  const [newPublisher, setNewPublisher] = useState('');
  const [newPublishedYear, setNewPublishedYear] = useState('');
  const [newPageCount, setNewPageCount] = useState('');
  const [newIsbn, setNewIsbn] = useState('');
  const [newRating, setNewRating] = useState(0);
  const [newLanguage, setNewLanguage] = useState(''); // Add language state

  // Initialize states when book data is loaded
  useEffect(() => {
    if (book) {
      setNewTitle(book.title || '');
      setNewAuthor(book.author || '');
      setNewDescription(book.description || '');
      setNewSubtitle(book.subtitle || '');
      setNewPublisher(book.publisher || '');
      setNewPublishedYear(book.publishedYear || '');
      setNewPageCount(book.pageCount ? String(book.pageCount) : '');
      setNewIsbn(book.isbn || '');
      setNewRating(book.rating || 0);
      setNewLanguage(book.language || '');
    }
  }, [book]);

  const handleTitleEditToggle = () => {
    setTitleEditing(!titleEditing);
    setNewTitle(book.title); // Reset to current value when toggling
  };

  const handleAuthorEditToggle = () => {
    setAuthorEditing(!authorEditing);
    setNewAuthor(book.author); // Reset to current value when toggling
  };

  const handleDescriptionEditToggle = () => {
    setDescriptionEditing(!descriptionEditing);
    setNewDescription(book.description); // Reset to current value when toggling
  };

  const handleSubtitleEditToggle = () => {
    setSubtitleEditing(!subtitleEditing);
    setNewSubtitle(book.subtitle || '');
  };

  const handlePublisherEditToggle = () => {
    setPublisherEditing(!publisherEditing);
    setNewPublisher(book.publisher || '');
  };

  const handlePublishedYearEditToggle = () => {
    setPublishedYearEditing(!publishedYearEditing);
    setNewPublishedYear(book.publishedYear || '');
  };

  const handlePageCountEditToggle = () => {
    setPageCountEditing(!pageCountEditing);
    setNewPageCount(book.pageCount ? String(book.pageCount) : '');
  };

  const handleIsbnEditToggle = () => {
    setIsbnEditing(!isbnEditing);
    setNewIsbn(book.isbn || '');
  };

  const handleRatingEditToggle = () => {
    setRatingEditing(!ratingEditing);
    setNewRating(book.rating || 0);
  };

  const handleLanguageEditToggle = () => {
    setLanguageEditing(!languageEditing);
    setNewLanguage(book.language || '');
  };

  const handleTitleChange = (e) => {
    setNewTitle(e.target.value);
  };

  const handleAuthorChange = (e) => {
    setNewAuthor(e.target.value);
  };

  const handleDescriptionChange = (e) => {
    setNewDescription(e.target.value);
  };

  const handleSubtitleChange = (e) => {
    setNewSubtitle(e.target.value);
  };

  const handlePublisherChange = (e) => {
    setNewPublisher(e.target.value);
  };

  const handlePublishedYearChange = (e) => {
    setNewPublishedYear(e.target.value);
  };

  const handlePageCountChange = (e) => {
    // Only allow numeric input
    const value = e.target.value.replace(/\D/g, '');
    setNewPageCount(value);
  };

  const handleIsbnChange = (e) => {
    // Allow only valid ISBN characters
    const value = e.target.value.replace(/[^0-9X-]/gi, '');
    setNewIsbn(value);
  };

  const handleRatingChange = (e) => {
    const value = parseFloat(e.target.value);
    setNewRating(value >= 0 && value <= 5 ? value : 0);
  };

  const handleLanguageChange = (e) => {
    setNewLanguage(e.target.value);
  };

  const handlePublishToggle = async () => {
    if (!book) return;
    
    const newStatus = book.status === 'published' ? 'draft' : 'published';
    
    try {
      setStatusUpdateLoading(true);
      
      // Use PATCH to update only the status field
      await booksAPI.patchBook(book.id, { status: newStatus });
      
      // Notify parent component
      if (onStatusChange) {
        onStatusChange(newStatus);
      }
      
      setSnackbar({
        open: true,
        message: `Book ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update book status',
        severity: 'error'
      });
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleTitleSave = async () => {
    if (!book || !newTitle || newTitle === book.title) {
      setTitleEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      
      // Use PATCH to update only the title field
      await booksAPI.patchBook(book.id, { title: newTitle });
      
      // Notify parent component with updated data
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, title: newTitle });
      }
      
      setTitleEditing(false);
      setSnackbar({
        open: true,
        message: 'Title updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating title:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update title',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  const handleAuthorSave = async () => {
    if (!book || !newAuthor || newAuthor === book.author) {
      setAuthorEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      
      // Use PATCH to update only the author field
      await booksAPI.patchBook(book.id, { author: newAuthor });
      
      // Notify parent component with updated data
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, author: newAuthor });
      }
      
      setAuthorEditing(false);
      setSnackbar({
        open: true,
        message: 'Author updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating author:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update author',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  const handleDescriptionSave = async () => {
    if (!book || !newDescription || newDescription === book.description) {
      setDescriptionEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      
      // Use PATCH to update only the description field
      await booksAPI.patchBook(book.id, { description: newDescription });
      
      // Notify parent component with updated data
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, description: newDescription });
      }
      
      setDescriptionEditing(false);
      setSnackbar({
        open: true,
        message: 'Description updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating description:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update description',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  const handleSubtitleSave = async () => {
    if (!book || newSubtitle === book.subtitle) {
      setSubtitleEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      await booksAPI.patchBook(book.id, { subtitle: newSubtitle });
      
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, subtitle: newSubtitle });
      }
      
      setSubtitleEditing(false);
      setSnackbar({
        open: true,
        message: 'Subtitle updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating subtitle:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update subtitle',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  const handlePublisherSave = async () => {
    if (!book || newPublisher === book.publisher) {
      setPublisherEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      await booksAPI.patchBook(book.id, { publisher: newPublisher });
      
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, publisher: newPublisher });
      }
      
      setPublisherEditing(false);
      setSnackbar({
        open: true,
        message: 'Publisher updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating publisher:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update publisher',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  const handlePublishedYearSave = async () => {
    if (!book || newPublishedYear === book.publishedYear) {
      setPublishedYearEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      await booksAPI.patchBook(book.id, { publishedYear: newPublishedYear });
      
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, publishedYear: newPublishedYear });
      }
      
      setPublishedYearEditing(false);
      setSnackbar({
        open: true,
        message: 'Published year updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating published year:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update published year',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  const handlePageCountSave = async () => {
    if (!book || (newPageCount === '' && !book.pageCount) || 
        (book.pageCount && String(book.pageCount) === newPageCount)) {
      setPageCountEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      const pageCount = newPageCount === '' ? null : parseInt(newPageCount);
      await booksAPI.patchBook(book.id, { pageCount });
      
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, pageCount });
      }
      
      setPageCountEditing(false);
      setSnackbar({
        open: true,
        message: 'Page count updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating page count:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update page count',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  const handleIsbnSave = async () => {
    if (!book || newIsbn === book.isbn) {
      setIsbnEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      await booksAPI.patchBook(book.id, { isbn: newIsbn });
      
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, isbn: newIsbn });
      }
      
      setIsbnEditing(false);
      setSnackbar({
        open: true,
        message: 'ISBN updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating ISBN:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update ISBN',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  const handleRatingSave = async () => {
    if (!book || newRating === book.rating) {
      setRatingEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      await booksAPI.patchBook(book.id, { rating: newRating });
      
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, rating: newRating });
      }
      
      setRatingEditing(false);
      setSnackbar({
        open: true,
        message: 'Rating updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating rating:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update rating',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  const handleLanguageSave = async () => {
    if (!book || newLanguage === book.language) {
      setLanguageEditing(false);
      return;
    }
    
    try {
      setMetadataUpdateLoading(true);
      await booksAPI.patchBook(book.id, { language: newLanguage });
      
      if (onStatusChange) {
        onStatusChange(book.status, { ...book, language: newLanguage });
      }
      
      setLanguageEditing(false);
      setSnackbar({
        open: true,
        message: 'Language updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating language:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update language',
        severity: 'error'
      });
    } finally {
      setMetadataUpdateLoading(false);
    }
  };

  // Extract categoryIds from the book's categories array if needed
  const getCategoryIds = () => {
    // If book already has categoryIds property, use it
    if (book?.categoryIds && Array.isArray(book.categoryIds)) {
      return book.categoryIds;
    }
    
    // Otherwise, extract IDs from the categories array
    if (book?.categories && Array.isArray(book.categories)) {
      return book.categories.map(category => category.id);
    }
    
    // Fallback to empty array
    return [];
  };

  // Function to handle category changes from CategoryManager
  const handleCategoryChange = (updatedCategoryIds) => {
    if (onStatusChange) {
      onStatusChange(book.status, { 
        ...book, 
        categoryIds: updatedCategoryIds,
        // Also update the categories array to maintain both data structures
        categories: updatedCategoryIds.map(id => {
          const category = categories.find(c => c.id === id);
          return category || { id };
        })
      });
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <BookCover 
            book={book}
            coverUpdateLoading={coverUpdateLoading}
            setCoverUpdateLoading={setCoverUpdateLoading}
            setSnackbar={setSnackbar}
            onStatusChange={onStatusChange}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          {titleEditing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TextField 
                value={newTitle} 
                onChange={handleTitleChange} 
                variant="outlined" 
                fullWidth
                sx={{ mr: 1 }}
              />
              <Button 
                variant="contained" 
                onClick={handleTitleSave}
                disabled={metadataUpdateLoading}
                startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              >
                Save
              </Button>
            </Box>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {book.title}
              </Typography>
              <Button 
                startIcon={<EditIcon />} 
                size="small" 
                onClick={handleTitleEditToggle}
              >
                Edit
              </Button>
            </Stack>
          )}
          
          {authorEditing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField 
                value={newAuthor} 
                onChange={handleAuthorChange} 
                variant="outlined" 
                fullWidth
                sx={{ mr: 1 }}
                size="small"
              />
              <Button 
                variant="contained" 
                onClick={handleAuthorSave}
                disabled={metadataUpdateLoading}
                startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                size="small"
              >
                Save
              </Button>
            </Box>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h6" color="text.secondary">
                {book.author}
              </Typography>
              <Button 
                startIcon={<EditIcon />} 
                size="small" 
                onClick={handleAuthorEditToggle}
              >
                Edit
              </Button>
            </Stack>
          )}

          {/* Add language display */}
          {book.language && !languageEditing && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LanguageIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Language: {book.language}
              </Typography>
            </Box>
          )}

          {descriptionEditing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField 
                value={newDescription} 
                onChange={handleDescriptionChange} 
                variant="outlined" 
                fullWidth
                sx={{ mr: 1 }}
                size="small"
                multiline
                rows={4}
              />
              <Button 
                variant="contained" 
                onClick={handleDescriptionSave}
                disabled={metadataUpdateLoading}
                startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                size="small"
              >
                Save
              </Button>
            </Box>
          ) : (
            <>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Description
                </Typography>
                <Button 
                  startIcon={<EditIcon />} 
                  size="small" 
                  onClick={handleDescriptionEditToggle}
                >
                  Edit
                </Button>
              </Stack>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 2,
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 5, // Limit to 5 lines
                  textOverflow: 'ellipsis',
                }}
              >
                {book.description || 'No description available.'}
              </Typography>
            </>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Chip 
                  label={book.status} 
                  color={book.status === 'published' ? 'success' : 'default'}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Button 
                  variant="contained" 
                  color={book.status === 'published' ? 'warning' : 'success'}
                  onClick={handlePublishToggle}
                  disabled={statusUpdateLoading}
                  startIcon={statusUpdateLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  size="small"
                >
                  {statusUpdateLoading ? 'Updating...' : 
                   book.status === 'published' ? 'Unpublish' : 'Publish'}
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Duration Options
              </Typography>
              <Box display="flex" gap={1} mt={0.5}>
                {book.durationOptions?.map((duration, idx) => (
                  <Chip key={idx} label={`${duration} min`} size="small" />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                ISBN
              </Typography>
              {isbnEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <TextField 
                    value={newIsbn} 
                    onChange={handleIsbnChange} 
                    variant="outlined" 
                    fullWidth
                    sx={{ mr: 1 }}
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleIsbnSave}
                    disabled={metadataUpdateLoading}
                    startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    size="small"
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="body2" mt={0.5}>
                    {book.isbn || 'Not available'}
                  </Typography>
                  <Button 
                    startIcon={<EditIcon />} 
                    size="small" 
                    onClick={handleIsbnEditToggle}
                    sx={{ ml: 1 }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Subtitle
              </Typography>
              {subtitleEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <TextField 
                    value={newSubtitle} 
                    onChange={handleSubtitleChange} 
                    variant="outlined" 
                    fullWidth
                    sx={{ mr: 1 }}
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleSubtitleSave}
                    disabled={metadataUpdateLoading}
                    startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    size="small"
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="body2" mt={0.5}>
                    {book.subtitle || 'Not available'}
                  </Typography>
                  <Button 
                    startIcon={<EditIcon />} 
                    size="small" 
                    onClick={handleSubtitleEditToggle}
                    sx={{ ml: 1 }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Publisher
              </Typography>
              {publisherEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <TextField 
                    value={newPublisher} 
                    onChange={handlePublisherChange} 
                    variant="outlined" 
                    fullWidth
                    sx={{ mr: 1 }}
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handlePublisherSave}
                    disabled={metadataUpdateLoading}
                    startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    size="small"
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="body2" mt={0.5}>
                    {book.publisher || 'Not available'}
                  </Typography>
                  <Button 
                    startIcon={<EditIcon />} 
                    size="small" 
                    onClick={handlePublisherEditToggle}
                    sx={{ ml: 1 }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Published Year
              </Typography>
              {publishedYearEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <TextField 
                    value={newPublishedYear} 
                    onChange={handlePublishedYearChange} 
                    variant="outlined" 
                    fullWidth
                    sx={{ mr: 1 }}
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handlePublishedYearSave}
                    disabled={metadataUpdateLoading}
                    startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    size="small"
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="body2" mt={0.5}>
                    {book.publishedYear || 'Not available'}
                  </Typography>
                  <Button 
                    startIcon={<EditIcon />} 
                    size="small" 
                    onClick={handlePublishedYearEditToggle}
                    sx={{ ml: 1 }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Page Count
              </Typography>
              {pageCountEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <TextField 
                    value={newPageCount} 
                    onChange={handlePageCountChange} 
                    variant="outlined" 
                    fullWidth
                    sx={{ mr: 1 }}
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handlePageCountSave}
                    disabled={metadataUpdateLoading}
                    startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    size="small"
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="body2" mt={0.5}>
                    {book.pageCount || 'Not available'}
                  </Typography>
                  <Button 
                    startIcon={<EditIcon />} 
                    size="small" 
                    onClick={handlePageCountEditToggle}
                    sx={{ ml: 1 }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Rating
              </Typography>
              {ratingEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <TextField 
                    value={newRating} 
                    onChange={handleRatingChange} 
                    variant="outlined" 
                    fullWidth
                    sx={{ mr: 1 }}
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 5, step: 0.1 }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleRatingSave}
                    disabled={metadataUpdateLoading}
                    startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    size="small"
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="body2" mt={0.5}>
                    {book.rating || 'Not available'}
                  </Typography>
                  <Button 
                    startIcon={<EditIcon />} 
                    size="small" 
                    onClick={handleRatingEditToggle}
                    sx={{ ml: 1 }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Grid>

            {/* Update language grid item to be editable */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Language
              </Typography>
              {languageEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <FormControl fullWidth size="small" sx={{ mr: 1 }}>
                    <Select
                      value={newLanguage}
                      onChange={handleLanguageChange}
                      displayEmpty
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="es">Spanish</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                      <MenuItem value="de">German</MenuItem>
                      <MenuItem value="it">Italian</MenuItem>
                      <MenuItem value="pt">Portuguese</MenuItem>
                      <MenuItem value="ru">Russian</MenuItem>
                      <MenuItem value="zh">Chinese</MenuItem>
                      <MenuItem value="ja">Japanese</MenuItem>
                      <MenuItem value="ko">Korean</MenuItem>
                      <MenuItem value="ar">Arabic</MenuItem>
                      <MenuItem value="hi">Hindi</MenuItem>
                      <MenuItem value="tr">Turkish</MenuItem>
                      <MenuItem value="pl">Polish</MenuItem>
                      <MenuItem value="nl">Dutch</MenuItem>
                      <MenuItem value="sv">Swedish</MenuItem>
                      <MenuItem value="da">Danish</MenuItem>
                      <MenuItem value="no">Norwegian</MenuItem>
                      <MenuItem value="fi">Finnish</MenuItem>
                    </Select>
                  </FormControl>
                  <Button 
                    variant="contained" 
                    onClick={handleLanguageSave}
                    disabled={metadataUpdateLoading}
                    startIcon={metadataUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    size="small"
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <LanguageIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {book.language || 'Not specified'}
                  </Typography>
                  <Button 
                    startIcon={<EditIcon />} 
                    size="small" 
                    onClick={handleLanguageEditToggle}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <CategoryManager 
                entityId={book?.id}
                entityType="book"
                categoryIds={getCategoryIds()}
                categories={categories}
                onChange={handleCategoryChange}
                setSnackbar={setSnackbar}
                addCategories={booksAPI.addCategoriesToBook}
                removeCategories={booksAPI.removeCategoriesFromBook}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BookHeaderCard;
